/**
 * Import team members from Jira project users (shared by ProfilePage and Onboarding).
 */

export type LoadTeamFromJiraFetch = (path: string, init?: RequestInit) => Promise<Response>;

export type JiraProjectUser =
  | string
  | {
      displayName?: string;
      name?: string;
      emailAddress?: string | null;
      email?: string | null;
      accountId?: string | null;
      key?: string | null;
      active?: boolean;
    };

export interface TeamMemberForJiraSync {
  id?: number;
  name: string;
  email?: string | null;
  jiraUsername?: string | null;
  capacityHoursPerDay?: number;
  jiraActive?: boolean | null;
  includeInCapacity?: boolean;
  isActive?: boolean;
  role?: string;
  includeInHistoricalCapacity?: boolean;
  excludeFromDistribution?: boolean;
  startDateInTeam?: string | null;
  endDateInTeam?: string | null;
  /** Set when roster is backed by roster_persons (migration 033+). */
  personId?: string;
  distributionCriteria?: {
    cluster: string[];
    component: string[];
    label: string[];
    priority: string[];
    customFields?: Record<string, string[]>;
  };
}

function normalizeMemberFromApi(m: Record<string, unknown>): TeamMemberForJiraSync {
  const includeRaw = m.includeInCapacity ?? m.include_in_capacity;
  let includeInCapacity = true;
  if (typeof includeRaw === 'number') includeInCapacity = includeRaw === 1;
  else if (typeof includeRaw === 'boolean') includeInCapacity = includeRaw;

  let jiraActive: boolean | null = null;
  if (m.jiraActive === 1 || m.jiraActive === true) jiraActive = true;
  else if (m.jiraActive === 0 || m.jiraActive === false) jiraActive = false;

  let personId: string | undefined;
  const pidRaw = m.personId ?? m.person_id;
  if (typeof pidRaw === 'string' && pidRaw.trim() !== '') personId = pidRaw.trim();
  else if (pidRaw != null) personId = String(pidRaw);

  const dist = m.distributionCriteria;
  let distributionCriteria: TeamMemberForJiraSync['distributionCriteria'];
  if (dist != null && typeof dist === 'object') {
    const cfRaw = (dist as { customFields?: unknown }).customFields;
    const customFields =
      cfRaw != null &&
      typeof cfRaw === 'object' &&
      !Array.isArray(cfRaw) &&
      Object.keys(cfRaw as object).every((k) => typeof k === 'string')
        ? (cfRaw as Record<string, unknown>)
        : undefined;
    const customFieldsParsed: Record<string, string[]> = {};
    if (customFields) {
      for (const [key, arr] of Object.entries(customFields)) {
        customFieldsParsed[key] = Array.isArray(arr)
          ? arr.filter((x) => typeof x === 'string').map(String)
          : [];
      }
    }

    distributionCriteria = {
      cluster: Array.isArray((dist as { cluster?: unknown }).cluster)
        ? ((dist as { cluster: string[] }).cluster as string[])
        : [],
      component: Array.isArray((dist as { component?: unknown }).component)
        ? ((dist as { component: string[] }).component as string[])
        : [],
      label: Array.isArray((dist as { label?: unknown }).label)
        ? ((dist as { label: string[] }).label as string[])
        : [],
      priority: Array.isArray((dist as { priority?: unknown }).priority)
        ? ((dist as { priority: string[] }).priority as string[])
        : [],
      ...(Object.keys(customFieldsParsed).length > 0 ? { customFields: customFieldsParsed } : {}),
    };
  } else {
    distributionCriteria = {
      cluster: [],
      component: [],
      label: [],
      priority: [],
      customFields: {},
    };
  }

  const cap =
    typeof m.capacityHoursPerDay === 'number'
      ? m.capacityHoursPerDay
      : typeof m.capacity_hours_per_day === 'number'
        ? m.capacity_hours_per_day
        : undefined;

  return {
    id: typeof m.id === 'number' ? m.id : Number(m.id),
    name: String(m.name ?? ''),
    email: (m.email as string) ?? null,
    jiraUsername: ((m.jiraUsername ?? m.jira_username) as string) ?? null,
    ...(cap !== undefined ? { capacityHoursPerDay: cap } : {}),
    includeInCapacity,
    isActive: (m.isActive ?? m.is_active) !== false,
    role: (m.role as string) || 'developer',
    includeInHistoricalCapacity:
      m.includeInHistoricalCapacity === true || m.include_in_historical_capacity === true,
    excludeFromDistribution:
      m.excludeFromDistribution === true || m.exclude_from_distribution === true,
    startDateInTeam:
      m.startDateInTeam != null && String(m.startDateInTeam).trim() !== ''
        ? String(m.startDateInTeam).trim()
        : m.start_date_in_team != null && String(m.start_date_in_team).trim() !== ''
          ? String(m.start_date_in_team).trim()
          : null,
    endDateInTeam:
      m.endDateInTeam != null && String(m.endDateInTeam).trim() !== ''
        ? String(m.endDateInTeam).trim()
        : m.end_date_in_team != null && String(m.end_date_in_team).trim() !== ''
          ? String(m.end_date_in_team).trim()
          : null,
    distributionCriteria,
    ...(personId ? { personId } : {}),
    ...(jiraActive !== null ? { jiraActive } : {}),
  };
}

function buildJiraUserList(data: {
  userObjects?: JiraProjectUser[];
  users?: string[];
}): JiraProjectUser[] {
  if (data.userObjects && data.userObjects.length > 0) return data.userObjects;
  if (data.users && data.users.length > 0) {
    return data.users.map((name) => ({
      displayName: name,
      emailAddress: null,
      accountId: null,
      key: null,
    }));
  }
  return [];
}

/** Aligns with Profile roster PUT `distributionCriteria` serialization. */
function serializeDistributionCriteriaForPatch(
  dc: NonNullable<TeamMemberForJiraSync['distributionCriteria']>
): string {
  const cf =
    dc.customFields != null &&
    typeof dc.customFields === 'object' &&
    !Array.isArray(dc.customFields)
      ? dc.customFields
      : {};
  return JSON.stringify({
    cluster: Array.isArray(dc.cluster) ? dc.cluster : [],
    component: Array.isArray(dc.component) ? dc.component : [],
    label: Array.isArray(dc.label) ? dc.label : [],
    priority: Array.isArray(dc.priority) ? dc.priority : [],
    customFields: cf,
  });
}

async function rosterPutExistingMemberViaJiraImport(
  fetchWithContext: LoadTeamFromJiraFetch,
  member: TeamMemberForJiraSync
): Promise<boolean> {
  if (!member.personId || member.id == null || Number.isNaN(Number(member.id))) return false;

  const includeInCapacityValue =
    member.includeInCapacity === true || member.includeInCapacity === undefined;

  const dc = member.distributionCriteria;
  const distributionCriteriaStr =
    dc && typeof dc === 'object'
      ? serializeDistributionCriteriaForPatch(dc)
      : serializeDistributionCriteriaForPatch({
          cluster: [],
          component: [],
          label: [],
          priority: [],
          customFields: {},
        });

  const res = await fetchWithContext(`/api/roster/persons/${encodeURIComponent(member.personId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      displayName: member.name.trim(),
      email: member.email || null,
      jiraUsername: member.jiraUsername || null,
      teamMemberPatches: {
        [String(member.id)]: {
          capacityHoursPerDay: member.capacityHoursPerDay ?? 8.0,
          isActive: member.isActive !== undefined ? Boolean(member.isActive) : true,
          includeInCapacity: includeInCapacityValue,
          role: member.role || 'developer',
          includeInHistoricalCapacity: member.includeInHistoricalCapacity === true,
          excludeFromDistribution: member.excludeFromDistribution === true,
          startDateInTeam: member.startDateInTeam ?? null,
          endDateInTeam: member.endDateInTeam ?? null,
          distributionCriteria: distributionCriteriaStr,
        },
      },
    }),
  });

  return res.ok;
}

export type LoadTeamFromJiraResult =
  | {
      ok: true;
      added: number;
      updated: number;
      skipped: number;
      members: { name: string; role: string }[];
    }
  | { ok: false; error: string };

/** Progress events for long-running import (e.g. onboarding modal). */
export type LoadTeamProgressEvent =
  | { type: 'log'; message: string }
  | { type: 'progress'; percent: number; label?: string };

export type LoadTeamFromJiraOptions = {
  onProgress?: (event: LoadTeamProgressEvent) => void;
  /** Current product UUID; required for roster writes (header `X-Product-Id` alone is insufficient). */
  productId?: string | null;
};

/**
 * Syncs Jira assignable users into team_members via the existing API.
 * @param fetchWithContext - e.g. ProfilePage's profileApiFetch (sets org + product headers)
 */
export async function loadTeamFromJira(
  fetchWithContext: LoadTeamFromJiraFetch,
  options?: LoadTeamFromJiraOptions
): Promise<LoadTeamFromJiraResult> {
  const emit = options?.onProgress;
  const log = (message: string) => emit?.({ type: 'log', message });
  const prog = (percent: number, label?: string) =>
    emit?.({ type: 'progress', percent: Math.min(100, Math.max(0, percent)), label });

  const productId = options?.productId != null ? String(options.productId).trim() : '';
  if (!productId) {
    log('Error: Missing product context. Select or refresh the current product before importing.');
    return {
      ok: false,
      error: 'Missing product context — select a product, then retry the Jira import.',
    };
  }

  prog(2, 'Connecting to Jira…');
  log('Fetching assignable users from Jira…');

  const usersRes = await fetchWithContext('/api/jira/project-users');
  if (!usersRes.ok) {
    const errBody = await usersRes.json().catch(() => ({}));
    const err = (errBody as { error?: string }).error || 'Failed to fetch Jira project users';
    log(`Error: ${err}`);
    return {
      ok: false,
      error: err,
    };
  }

  const data = (await usersRes.json()) as {
    userObjects?: JiraProjectUser[];
    users?: string[];
    error?: string;
  };

  const jiraUsers = buildJiraUserList(data);
  if (jiraUsers.length === 0) {
    const err =
      data.error ||
      'No users found from Jira. Connect Jira in Settings and ensure the project has assignable users.';
    log(`Error: ${err}`);
    return {
      ok: false,
      error: err,
    };
  }

  log(`Received ${jiraUsers.length} user(s) from Jira.`);
  prog(12, 'Loading current team…');

  const currentRes = await fetchWithContext('/api/team-members?includeInactive=1');
  const currentData = currentRes.ok ? await currentRes.json() : { members: [] };
  const rawMembers = (currentData as { members?: Record<string, unknown>[] }).members || [];
  const currentMembers = rawMembers.map((m) => normalizeMemberFromApi(m));

  log(`Relix has ${currentMembers.length} team member(s) on file.`);
  prog(18, 'Syncing users…');

  let added = 0;
  let updated = 0;
  let skipped = 0;
  const totalJira = jiraUsers.length;

  const applyTeamMemberPatchesForNewSlice = async (personAgg: Record<string, unknown>) => {
    const products = Array.isArray(personAgg.products)
      ? (personAgg.products as Record<string, unknown>[])
      : [];
    const slice = products.find(
      (pr) => String(pr.productId ?? '').trim() === String(productId).trim()
    );
    const tmRaw = slice?.teamMemberId;
    if (tmRaw == null) return;
    const tmId = typeof tmRaw === 'number' ? tmRaw : Number(tmRaw);
    if (!Number.isFinite(tmId)) return;
    /** Defaults mirror legacy POST /api/team-members for Jira import: off capacity until opted in. */
    await fetchWithContext(`/api/roster/persons/${encodeURIComponent(String(personAgg.id))}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        teamMemberPatches: {
          [String(tmId)]: {
            capacityHoursPerDay: 8.0,
            includeInCapacity: false,
            isActive: true,
            includeInHistoricalCapacity: false,
            excludeFromDistribution: false,
            role: 'developer',
          },
        },
      }),
    }).catch(() => {});
  };

  for (let idx = 0; idx < jiraUsers.length; idx++) {
    const user = jiraUsers[idx];
    const userEmail = typeof user === 'string' ? null : user.emailAddress || user.email || null;
    const userName = typeof user === 'string' ? user : user.displayName || user.name || '';
    const jiraUsername = typeof user === 'string' ? null : user.accountId || user.key || null;

    const slice = totalJira > 0 ? (idx + 1) / totalJira : 1;
    prog(18 + slice * 72, `Syncing ${idx + 1}/${totalJira}…`);

    if (!userName) {
      log(`— (row ${idx + 1}: no display name, skipped)`);
      continue;
    }

    log(`— ${userName}${userEmail ? ` <${userEmail}>` : ''}`);

    const existingMember = currentMembers.find(
      (m) =>
        (m.email && userEmail && m.email.toLowerCase() === userEmail.toLowerCase()) ||
        (m.name && userName && m.name.toLowerCase() === userName.toLowerCase())
    );

    if (existingMember) {
      const needsUpdate =
        (userEmail && existingMember.email !== userEmail) ||
        (jiraUsername && existingMember.jiraUsername !== jiraUsername);

      if (needsUpdate) {
        const currentIncludeInCapacity =
          existingMember.includeInCapacity !== undefined ? existingMember.includeInCapacity : true;
        const candidate: TeamMemberForJiraSync = {
          ...existingMember,
          email: userEmail || existingMember.email,
          jiraUsername: jiraUsername || existingMember.jiraUsername,
          includeInCapacity: currentIncludeInCapacity,
        };
        let ok = false;
        if (candidate.personId) {
          ok = await rosterPutExistingMemberViaJiraImport(fetchWithContext, candidate);
        }
        /**
         * Without person_id linking, rely on PUT /team-members historically — now deprecated (410).
         * Row should receive person_id via migration/backfill before Jira imports are run.
         */
        if (!ok && !candidate.personId && existingMember.id != null) {
          const fallback = await fetchWithContext(`/api/team-members/${existingMember.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: candidate.name,
              email: candidate.email || null,
              jiraUsername: candidate.jiraUsername || null,
              capacityHoursPerDay: candidate.capacityHoursPerDay ?? 8.0,
              isActive: candidate.isActive !== undefined ? candidate.isActive : true,
              includeInCapacity: currentIncludeInCapacity,
              role: candidate.role || 'developer',
              includeInHistoricalCapacity: candidate.includeInHistoricalCapacity === true,
              excludeFromDistribution: candidate.excludeFromDistribution === true,
              startDateInTeam: candidate.startDateInTeam ?? null,
              endDateInTeam: candidate.endDateInTeam ?? null,
              distributionCriteria: candidate.distributionCriteria ?? {
                cluster: [],
                component: [],
                label: [],
                priority: [],
              },
            }),
          });
          ok = fallback.ok && fallback.status !== 410;
        }
        if (ok) {
          updated++;
          log(`  updated existing member`);
        }
      } else {
        skipped++;
        log(`  skipped (already in sync)`);
      }
    } else {
      const isInactiveInJira = typeof user === 'object' && user !== null && user.active === false;
      const addRes = await fetchWithContext('/api/roster/persons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          displayName: userName,
          email: userEmail || null,
          jiraUsername: jiraUsername || null,
          productIds: [productId],
        }),
      });
      let okPost = false;
      let createdAgg: Record<string, unknown> | null = null;
      if (addRes.ok && addRes.status === 201) {
        okPost = true;
        createdAgg = (await addRes.json().catch(() => null)) as Record<string, unknown> | null;
      }
      /** Legacy path if roster POST unexpectedly fails — should not normally happen once migration is applied. */
      if (!okPost) {
        const legacy = await fetchWithContext('/api/team-members', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: userName,
            email: userEmail || null,
            jiraUsername: jiraUsername || null,
            capacityHoursPerDay: 8.0,
            includeInCapacity: false,
            role: 'developer',
            ...(isInactiveInJira ? { jiraActive: false } : {}),
          }),
        });
        if (legacy.ok && legacy.status !== 410) {
          okPost = true;
        } else {
          const raw = await legacy.text().catch(() => '');
          log(
            `  failed to add roster person: ${addRes.status} / legacy ${legacy.status} ${raw.slice(0, 120)}`
          );
        }
      }

      if (okPost && createdAgg?.id) {
        added++;
        await applyTeamMemberPatchesForNewSlice(createdAgg).catch(() => {});
        log(`  added new member`);
      } else if (okPost && !createdAgg?.id) {
        added++;
        log(`  added new member`);
      }
    }
  }

  log(`Summary: +${added} added, ${updated} updated, ${skipped} skipped.`);
  prog(92, 'Refreshing team list…');

  const refreshRes = await fetchWithContext('/api/team-members?includeInactive=1');
  const members: { name: string; role: string }[] = [];
  if (refreshRes.ok) {
    const refreshed = await refreshRes.json();
    const list = (refreshed as { members?: Record<string, unknown>[] }).members || [];
    for (const row of list) {
      const n = normalizeMemberFromApi(row);
      if (n.name) members.push({ name: n.name, role: n.role || 'developer' });
    }
  }

  prog(100, 'Done');
  log(`Import finished. Total in team: ${members.length}.`);

  return { ok: true, added, updated, skipped, members };
}
